import { useProposalForm } from '@/features/proposals/hooks/use-proposal-form.hook';
import type {
  GroupLifeFrontStipulator,
  GroupLifeFrontSubStipulator,
} from '@/services/group-life';
import { Text } from '@noorden-insurtech/alba-design-system';
import { DateTime } from 'luxon';
import { useMemo, useRef } from 'react';
import type { PmeProposalStepProps } from '../proposal.types';
import {
  mapStipulatorValues,
  type PmeStipulantValues,
} from '../utils/hydrate-proposal.util';
import { PmeStepFooter } from './sections/step-footer.component';
import {
  PmeStipulantFormSection,
  type PmeStipulantFormSectionHandle,
} from './sections/stipulant-form-section.component';
import {
  PmeSubStipulantsSection,
  type PmeSubStipulantsSectionHandle,
} from './sections/sub-stipulants-section.component';
import { normalizeEmail } from '@/shared/utils/normalize-email.util';

type Props = PmeProposalStepProps & {
  stipulator?: GroupLifeFrontStipulator;
  subStipulators?: GroupLifeFrontSubStipulator[];
  initialEffectiveDate?: string;
};

/**
 * Monta o objeto de endereço no shape que o back (`applyStepInsurance`) espera,
 * ou `undefined` quando nenhum campo foi preenchido (não sobrescreve o snapshot).
 */
function buildAddressPayload(
  values: PmeStipulantValues,
): Record<string, string> | undefined {
  const address = {
    zipCode: values.cep?.trim() ?? '',
    street: values.street?.trim() ?? '',
    number: values.number?.trim() ?? '',
    complement: values.complement?.trim() ?? '',
    neighborhood: values.neighborhood?.trim() ?? '',
    city: values.city?.trim() ?? '',
    state: values.state?.trim() ?? '',
  };
  return Object.values(address).some(Boolean) ? address : undefined;
}

export function StepPmeInsureData({
  onNext,
  onBack,
  canGoBack,
  canGoNext,
  saving,
  isViewMode = false,
  stipulator,
  subStipulators,
  initialEffectiveDate,
}: Props) {
  const form = useProposalForm({
    defaultValues: { effectiveStartDate: initialEffectiveDate ?? '' },
    onSubmit: async () => {},
  });

  const stipulantRef = useRef<PmeStipulantFormSectionHandle>(null);
  const subsRef = useRef<PmeSubStipulantsSectionHandle>(null);

  const stipulatorValues = useMemo(
    () => mapStipulatorValues(stipulator),
    [stipulator],
  );

  const minDate = DateTime.now().plus({ days: 1 }).toISODate() ?? undefined;

  const handleNext = async () => {
    if (isViewMode) {
      onNext();
      return;
    }
    const [effErrors, stipulantValid, subsValid] = await Promise.all([
      form.validateField('effectiveStartDate', 'change'),
      stipulantRef.current?.validateRequired() ?? Promise.resolve(true),
      subsRef.current?.validateRequired() ?? Promise.resolve(true),
    ]);
    const effValid = !(Array.isArray(effErrors) && effErrors.length > 0);
    if (!effValid || !stipulantValid || !subsValid) return;

    const effectiveDate = form.state.values.effectiveStartDate?.trim();
    const stipulant = stipulantRef.current?.getValues();

    const payload: Record<string, unknown> = {};
    if (effectiveDate) payload.effectiveDate = effectiveDate;
    if (stipulant) {
      payload.email = normalizeEmail(stipulant.email);
      payload.phone = stipulant.phone?.trim() ?? '';
      const address = buildAddressPayload(stipulant);
      if (address) payload.address = address;
      // CAEPF (pessoa física): sexo e data de nascimento do titular para o i4Pro.
      if (stipulant.isCaepf) {
        if (stipulant.gender) payload.genderCode = stipulant.gender;
        if (stipulant.dateOfBirth) payload.dateOfBirth = stipulant.dateOfBirth;
      }
    }

    // Subestipulantes: endereço + contato de cada um (e Sexo/Data quando CAEPF),
    // no mesmo shape do estipulante, para persistir na proposta e no i4Pro.
    const subPayload = (subsRef.current?.getValues() ?? [])
      .map(({ documentNumber, values }) => {
        const entry: Record<string, unknown> = { documentNumber };
        const email = normalizeEmail(values.email);
        const phone = values.phone?.trim();
        if (email) entry.email = email;
        if (phone) entry.phone = phone;
        const address = buildAddressPayload(values);
        if (address) entry.address = address;
        if (values.isCaepf) {
          if (values.gender) entry.genderCode = values.gender;
          if (values.dateOfBirth) entry.dateOfBirth = values.dateOfBirth;
        }
        return entry;
      })
      .filter(entry => Object.keys(entry).length > 1);
    if (subPayload.length > 0) payload.subStipulators = subPayload;

    onNext(Object.keys(payload).length > 0 ? payload : undefined);
  };

  return (
    <div className='flex flex-col gap-10'>
      <div className='flex flex-col gap-6'>
        <Text className='text-base font-normal text-alba-black-500'>
          Dados da Cotação
        </Text>
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
          <form.AppField
            name='effectiveStartDate'
            validators={{
              onChange: ({ value }: { value: unknown }) =>
                String(value ?? '').trim()
                  ? undefined
                  : 'Informe o início da vigência',
            }}
            children={field => (
              <field.CalendarField
                label='Início da Vigência'
                placeholder='Selecione a data'
                required
                minDate={minDate}
                disabled={isViewMode}
              />
            )}
          />
        </div>
      </div>

      <PmeStipulantFormSection
        ref={stipulantRef}
        showContact
        values={stipulatorValues}
        requireAddressFields
        isViewMode={isViewMode}
      />

      <PmeSubStipulantsSection
        ref={subsRef}
        subStipulators={subStipulators}
        isViewMode={isViewMode}
      />

      <PmeStepFooter
        onBack={onBack}
        canGoBack={canGoBack}
        onNext={handleNext}
        canGoNext={canGoNext && !saving}
      />
    </div>
  );
}
